import type { CSSProperties, ReactNode } from 'react';

import { color, colorCategoria, suave } from '#components/mobile/ui/tokens';

/** Las cinco metas del paso 6 (mismos ids que la maqueta). */
export type IdMeta = 'colchon' | 'viaje' | 'compra' | 'carne' | 'otra';

/** Hueco de color de cada meta (fondo del dibujo), como la maqueta. */
export const HUECO_META: Record<IdMeta, number> = {
  colchon: 7,
  viaje: 0,
  compra: 8,
  carne: 5,
  otra: 10,
};

/**
 * Tintas de la ilustración: fijas en claro y oscuro, como en la maqueta (los
 * dibujos no cambian con el tema; el fondo y algunos trazos sí, con tokens).
 */
const TINTA = {
  rojo: '#f0616a',
  blanco: '#ffffff',
  papel: '#eef1f8',
  papelSombra: '#c9d3e6',
  ambar: '#e8b04b',
  ambarOscuro: '#a86d0f',
  pizarra: '#3c4a6b',
  pantalla: '#8fd3b0',
  base: '#a7b0c8',
  semaforo: '#25304f',
  luzApagada: '#5c647d',
  luzVerde: '#3fae7a',
  madera: '#c79a6a',
  estrella: '#f2c94c',
} as const;

const DIBUJOS: Record<IdMeta, ReactNode> = {
  // Fondo de emergencia: salvavidas.
  colchon: (
    <>
      <circle cx="32" cy="33" r="17" fill={TINTA.rojo} />
      <circle cx="32" cy="33" r="8" fill={color.surface} />
      <path
        d="M20 21 L25 26 M44 21 L39 26 M20 45 L25 40 M44 45 L39 40"
        stroke={TINTA.blanco}
        strokeWidth="5"
      />
      <path
        d="M48 18 Q56 12 54 26"
        stroke={color.fg3}
        strokeWidth="2"
        fill="none"
      />
    </>
  ),
  // Viaje: avión de papel y maleta.
  viaje: (
    <>
      <path
        d="M12 36 L52 18 L40 46 L32 38 Z"
        fill={TINTA.papel}
        stroke={color.fg3}
        strokeWidth="1.5"
      />
      <path d="M32 38 L52 18 L29 33 Z" fill={TINTA.papelSombra} />
      <rect x="14" y="42" width="12" height="9" rx="2" fill={TINTA.ambar} />
      <path
        d="M17 42 v-3 h6 v3"
        stroke={TINTA.ambarOscuro}
        strokeWidth="1.6"
        fill="none"
      />
    </>
  ),
  // Compra grande: portátil con lazo.
  compra: (
    <>
      <rect x="15" y="18" width="34" height="22" rx="3" fill={TINTA.pizarra} />
      <rect
        x="18"
        y="21"
        width="28"
        height="16"
        rx="1.5"
        fill={TINTA.pantalla}
      />
      <path d="M10 42 h44 l-3 5 H13 z" fill={TINTA.base} />
      <path
        d="M32 18 v29 M22 30 h20"
        stroke={TINTA.rojo}
        strokeWidth="3"
        opacity=".9"
      />
      <circle cx="32" cy="18" r="3.5" fill={TINTA.rojo} />
    </>
  ),
  // Carné de conducir: volante y semáforo.
  carne: (
    <>
      <circle
        cx="32"
        cy="34"
        r="16"
        fill="none"
        stroke={TINTA.pizarra}
        strokeWidth="5"
      />
      <path
        d="M32 34 L32 50 M32 34 L18 28 M32 34 L46 28"
        stroke={TINTA.pizarra}
        strokeWidth="4"
      />
      <rect x="44" y="8" width="11" height="24" rx="4" fill={TINTA.semaforo} />
      <circle cx="49.5" cy="14" r="2.6" fill={TINTA.luzApagada} />
      <circle cx="49.5" cy="20" r="2.6" fill={TINTA.luzApagada} />
      <circle cx="49.5" cy="26" r="2.8" fill={TINTA.luzVerde} />
    </>
  ),
  // Otra meta: cofre con estrella.
  otra: (
    <>
      <path
        d="M14 34 h36 v14 a3 3 0 0 1-3 3 H17 a3 3 0 0 1-3-3 z"
        fill={TINTA.ambarOscuro}
      />
      <path d="M14 34 l4-10 h28 l4 10 z" fill={TINTA.madera} />
      <path
        d="M32 12 l3.5 7.5 8 .8-6 5.4 1.8 8-7.3-4.2-7.3 4.2 1.8-8-6-5.4 8-.8z"
        fill={TINTA.estrella}
      />
      <rect x="29" y="38" width="6" height="7" rx="1" fill={TINTA.ambar} />
    </>
  ),
};

type DibujoMetaProps = {
  meta: IdMeta;
  /** Lado en px (52 en las tarjetas del paso 6). */
  size?: number;
  style?: CSSProperties;
};

/**
 * Dibujo propio de una meta (fondo de emergencia, viaje, compra grande,
 * carné, otra meta) sobre un círculo teñido con su color de categoría. Sin
 * letras ni cifras.
 */
export function DibujoMeta({ meta, size = 52, style }: DibujoMetaProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      aria-hidden
      data-testid={`dibujo-meta-${meta}`}
      style={{ flex: 'none', ...style }}
    >
      <circle
        cx="32"
        cy="32"
        r="30"
        fill={suave(colorCategoria(HUECO_META[meta]), 20)}
      />
      {DIBUJOS[meta]}
    </svg>
  );
}
